import React, { FC, useState, useEffect, useCallback } from 'react';

import { ReactComponent as SearchIcon } from 'assets/icons/Search.svg';
import { ReactComponent as LockIcon } from 'assets/icons/LockLarge.svg';

import sparkIconDark from 'assets/videos/spark-icon-dark.webm';
import usePageType from 'helpers/usePageType';
import useDiscoveryTab from 'helpers/useDiscoveryTab';
import { useProfileContext } from 'helpers/useProfileProvider';
import withHapticFeedback from 'helpers/withHapticFeedback';
import { EExploreTabs } from 'components/Explore/ExploreTabs/helpers/constants';
import SparkSelectionModal from 'components/Profile/SparksList/SparkSelectionModal';
import {
  StyledFloatingMenuContainer,
  StyledFloatingMenu,
  StyledFloatingButton,
  StyledTabButton,
  StyledSparkCreationButton,
} from './styled';

// Profile icon buttons
export type ProfileIconButton = 'search' | 'lock' | 'layout';

// Discovery tab buttons
export type DiscoveryTabButton = 'featured' | 'following' | 'channels';

// Union type for all button types
export type FloatingMenuButton = ProfileIconButton | DiscoveryTabButton;

// Menu variant type
export type FloatingMenuVariant = 'profile' | 'discovery';

interface IProfileFloatingMenuProps {
  variant?: FloatingMenuVariant;
  visibleButtons?: FloatingMenuButton[];
  activeButton?: FloatingMenuButton | null;
  onButtonClick?: (button: FloatingMenuButton) => void;
}

const DISCOVERY_TAB_LABELS: Record<DiscoveryTabButton, string> = {
  featured: 'Featured',
  following: 'Following',
  channels: 'Channels',
};

// Map between our button types and EExploreTabs
const BUTTON_TO_EXPLORE_TAB: Record<DiscoveryTabButton, EExploreTabs> = {
  featured: EExploreTabs.Featured,
  following: EExploreTabs.Following,
  channels: EExploreTabs.Channels,
};

const EXPLORE_TAB_TO_BUTTON: Record<string, DiscoveryTabButton> = {
  [EExploreTabs.Featured]: 'featured',
  [EExploreTabs.Following]: 'following',
  [EExploreTabs.Channels]: 'channels',
};

const ProfileFloatingMenu: FC<IProfileFloatingMenuProps> = ({
  variant = 'profile',
  visibleButtons,
  activeButton: controlledActiveButton,
  onButtonClick,
}) => {
  const { isExactlyProfile, isDiscovery } = usePageType();
  const { discoveryTab, setDiscoveryTab } = useDiscoveryTab();
  const { activeTabIndex: profileActiveTabIndex, hasSparkCategories } = useProfileContext();

  // Sparks tab is index 1 in the profile tabs, but only when spark categories exist
  // Use explicit comparison to handle undefined case
  const isSparksTabActive = variant === 'profile' && hasSparkCategories && profileActiveTabIndex === 1;

  // Set default visible buttons based on variant
  const defaultButtons: FloatingMenuButton[] =
    variant === 'discovery' ? ['featured', 'following', 'channels'] : ['search', 'lock', 'layout'];
  const buttonsToShow = visibleButtons ?? defaultButtons;

  const [isVisible, setIsVisible] = useState(false);
  const [firstCardTop, setFirstCardTop] = useState<number | null>(null);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [internalActiveButton, setInternalActiveButton] = useState<FloatingMenuButton | null>(null);
  const [isSparkModalOpen, setIsSparkModalOpen] = useState(false);

  // For discovery variant, derive active button from shared context
  // For profile variant, use controlled value or internal state
  const getActiveButton = (): FloatingMenuButton | null => {
    if (variant === 'discovery') {
      return (discoveryTab ? EXPLORE_TAB_TO_BUTTON[discoveryTab] : 'featured') ?? 'featured';
    }
    if (controlledActiveButton !== undefined) {
      return controlledActiveButton;
    }
    return internalActiveButton;
  };
  const activeButton = getActiveButton();

  // Find the first card boundary on mount and when content changes
  const updateFirstCardPosition = useCallback(() => {
    let targetElement: Element | null = null;

    if (variant === 'discovery') {
      // For Discovery pages, look for the first card in the feed content
      // Try to find the first UserCard or card element below the fixed tabs
      targetElement =
        document.querySelector('[class*="UserCard"]') ||
        document.querySelector('[class*="CardWrap"]') ||
        document.querySelector('[data-tour]');
    } else {
      // For Profile pages, look for the spotlight container or list grid
      targetElement = document.getElementById('spotlight-container') || document.querySelector('[class*="ListGrid"]');
    }

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setFirstCardTop(rect.top + window.scrollY);
    }
  }, [variant]);

  useEffect(() => {
    // Initial position calculation with a delay to ensure content is rendered
    const timer = setTimeout(updateFirstCardPosition, 500);

    // Recalculate on resize
    window.addEventListener('resize', updateFirstCardPosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateFirstCardPosition);
    };
  }, [updateFirstCardPosition]);

  useEffect(() => {
    const handleScroll = () => {
      if (firstCardTop === null) {
        updateFirstCardPosition();
        return;
      }

      const { scrollY } = window;

      const isPastFirstCard = scrollY >= firstCardTop;

      // Show menu only when:
      // 1. User has scrolled past the first card boundary AND
      // 2. User is scrolling UP
      // Hide menu when scrolling down or above the first card
      setIsVisible(isPastFirstCard);
      setLastScrollY(scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [firstCardTop, lastScrollY, updateFirstCardPosition]);

  // Only show on appropriate pages based on variant
  const shouldShowOnPage = variant === 'discovery' ? isDiscovery : isExactlyProfile;
  if (!shouldShowOnPage) {
    return null;
  }

  // Don't render if no buttons to show
  if (buttonsToShow.length === 0) {
    return null;
  }

  const handleButtonClick = (button: FloatingMenuButton) => {
    if (variant === 'discovery') {
      // For discovery tabs, use the shared context (same as ExploreTabs)
      const exploreTab = BUTTON_TO_EXPLORE_TAB[button as DiscoveryTabButton];
      if (exploreTab) {
        withHapticFeedback(() => setDiscoveryTab?.(exploreTab))();
      }
    } else {
      // For profile buttons, toggle behavior
      const newActiveButton = activeButton === button ? null : button;
      setInternalActiveButton(newActiveButton);
    }
    onButtonClick?.(button);
  };

  const renderProfileButtons = () => (
    <>
      {buttonsToShow.includes('search') && (
        <StyledFloatingButton
          isActive={activeButton === 'search'}
          onClick={() => handleButtonClick('search')}
          aria-label="Search"
        >
          <SearchIcon />
        </StyledFloatingButton>
      )}
      {buttonsToShow.includes('lock') && (
        <StyledFloatingButton
          isActive={activeButton === 'lock'}
          onClick={() => handleButtonClick('lock')}
          aria-label="Lock"
        >
          <LockIcon />
        </StyledFloatingButton>
      )}
      {/* {buttonsToShow.includes('layout') && (
        <StyledFloatingButton
          isActive={activeButton === 'layout'}
          onClick={() => handleButtonClick('layout')}
          aria-label="Layout switch"
        >
          <LayoutSwitchIcon />
        </StyledFloatingButton>
      )} */}
    </>
  );

  const renderDiscoveryTabs = () => (
    <>
      {(buttonsToShow as DiscoveryTabButton[]).map((tab) => (
        <StyledTabButton
          key={tab}
          isActive={activeButton === tab}
          onClick={() => handleButtonClick(tab)}
          aria-label={DISCOVERY_TAB_LABELS[tab as DiscoveryTabButton]}
        >
          {DISCOVERY_TAB_LABELS[tab as DiscoveryTabButton]}
        </StyledTabButton>
      ))}
    </>
  );

  const handleOpenSparkModal = () => {
    withHapticFeedback(() => setIsSparkModalOpen(true))();
  };

  const handleCloseSparkModal = () => {
    setIsSparkModalOpen(false);
  };

  return (
    <>
      <StyledFloatingMenuContainer isVisible={isVisible} isSparksTabActive={isSparksTabActive}>
        <StyledFloatingMenu>
          {variant === 'profile' ? renderProfileButtons() : renderDiscoveryTabs()}
        </StyledFloatingMenu>
        {variant === 'profile' && isSparksTabActive && (
          <StyledSparkCreationButton onClick={handleOpenSparkModal} aria-label="Create Spark">
            <video src={sparkIconDark} autoPlay loop muted playsInline />
          </StyledSparkCreationButton>
        )}
      </StyledFloatingMenuContainer>
      <SparkSelectionModal isOpen={isSparkModalOpen} onClose={handleCloseSparkModal} />
    </>
  );
};

export default ProfileFloatingMenu;
